const { getElements, parseInteger } = window.BAO.Utils
class QuickBuyAsync extends window.BAO.CustomElement() {
  static get observedAttributes () {
    return ['section-id', 'url', 'loading', 'cache-key', 'cache-expiry']
  }

  static get requiredAttributes () {
    return ['section-id']
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(
      document.documentElement,
      'click',
      this.handleClick.bind(this),
    )
  }

  handleClick (event) {
    this.triggerEl = event.target.closest(
      '[data-quick-buy-async-external-el="button"]',
    )
    if (!this.triggerEl) return

    const searchResultsContainer = this.triggerEl.closest(
      '.kuQuickSearchResultsContainer',
    )
    if (searchResultsContainer) {
      const displayStyle = window.getComputedStyle(searchResultsContainer)
        .display
      if (displayStyle !== 'none') return
    }

    this.triggerEl.classList.add('btn-Button-loading')
    const buttonUrl = this.triggerEl.dataset.productUrl

    this.setAttribute('url', buttonUrl)

    if (!this.useCache && this.isLazy && this.isIntersectionObserverSupported) {
      this.intersectionObserverCallback = this.intersectionObserverCallback.bind(
        this,
      )
      this.observer.observe(this)
    } else {
      this.renderSection()
    }

    const nostoCheck = this.triggerEl
      .closest('section-async')
      ?.url?.includes('nosto=')

    if (!nostoCheck) return

    document.body.dataset.nostoProductId = this.triggerEl.closest(
      'section-async',
    ).dataset.productId
    document.body.dataset.nostoElementId = this.triggerEl.closest(
      '.nosto_element',
    ).id
  }

  /**
   * Renders the custom element
   */
  async renderSection () {
    this.isBusy = true
    const cachedHtml = this.cachedHtml
    const html =
      cachedHtml ||
      (await this.sectionRenderer.fetchSingle(this.sectionId, this.sectionUrl))
    this.sectionHTML = this.sectionRenderer.getInnerHtml(html)
    this.isBusy = false
    if (this.useCache && !cachedHtml) {
      this.storeHtmlInCache(html)
    }

    const variantDrawerSelector = document.querySelector(
      '.qb-QuickBuy_FormButton',
    )
    const drawerCloseIcon = document.querySelector('.qb-QuickBuy_Close')
    const drawerCloseOverlay = document.querySelector(
      '[data-site-drawers-el="backdrop"]',
    )

    if (variantDrawerSelector !== null) {
      variantDrawerSelector.addEventListener('click', this.nostoTracking)

      drawerCloseIcon.addEventListener('click', () => {
        variantDrawerSelector.removeEventListener('click', this.nostoTracking)
      })
      drawerCloseOverlay.addEventListener('click', () => {
        variantDrawerSelector.removeEventListener('click', this.nostoTracking)
      })
    }

    const modal = this.closest('quick-buy-modal')
    if (modal?.show) {
      modal.show()
    }

    window.BAO.dispatchEvent(window.BAO.EVENTS.OPEN_DRAWER, {
      key: 'productQuickBuy',
    })
    this.triggerEl.classList.remove('btn-Button-loading')
  }

  nostoTracking () {
    const nostoProductId = document.body.dataset.nostoProductId
    const nostoElementId = document.body.dataset.nostoElementId
    // if the data attributes added to the body aren't present, early return
    if (!nostoProductId) return

    // eslint-disable-next-line no-undef
    nostojs(api => {
      api.recommendedProductAddedToCart(nostoProductId, nostoElementId)
    })

    // clear the data attributes
    document.body.dataset.nostoProductId = ''
    document.body.dataset.nostoElementId = ''
  }

  /**
   * Removes the parent element
   */
  removeParentElement () {
    const elementToRemove = this.parentIdentifier
      ? this.closest(this.parentIdentifier)
      : this
    elementToRemove.remove()
  }

  /**
   * Calls the initialise function when the custom element is intersecting (visible in the viewport)
   */
  intersectionObserverCallback (entries) {
    const entry = entries?.[0]
    if (!entry?.isIntersecting) return
    this.renderSection()
    this.observer.disconnect()
  }

  /**
   * @returns {IntersectionObserver} an observer that specifies when an element is intersecting (visible in viewport)
   */
  get observer () {
    // eslint-disable-next-line compat/compat
    return new IntersectionObserver(this.intersectionObserverCallback)
  }

  /**
   * @returns {string} The ID of the section to be rendered
   */
  get sectionId () {
    return this.getAttribute('section-id')
  }

  /**
   * @param {string} html - HTML to be rendered
   */
  set sectionHTML (html) {
    if (!html) {
      /*
       * When there isn't any HTML we want to remove the parent element
       * to ensure we're not showing other elements of the section
       * such as kicker and title
       */
      this.removeParentElement()
      return
    }
    this.innerHTML = html
    this.enableDependantElements()
  }

  enableDependantElements () {
    getElements(`[data-await='${this.sectionId}']`).forEach(
      el => (el.disabled = false),
    )
  }

  storeHtmlInCache (html) {
    const now = new Date()
    const item = {
      value: html,
      expiry: now.getTime() + this.cacheExpiryInMilliseconds,
    }
    localStorage.setItem(this.cacheKey, JSON.stringify(item))
  }

  /**
   * @returns {string} html - HTML of the section
   */
  get sectionHTML () {
    return this.innerHTML
  }

  /**
   * @param {boolean} isBusy - Whether or not the element is currently busy
   */
  set isBusy (isBusy) {
    this.setAttribute('aria-busy', String(isBusy))
  }

  /**
   * @returns {boolean} Whether or not the element is currently busy
   */
  get isBusy () {
    return this.getAttribute('aria-busy') === 'true'
  }

  /**
   * @returns {sectionRenderer} The BAO sectionRenderer class
   */
  get sectionRenderer () {
    return window.BAO.sectionRenderer
  }

  /**
   * @returns {string} The URL from which to request the section
   */
  get url () {
    return this.getAttribute('url')?.replace(
      '[[query_parameters]]',
      window.location.search,
    )
  }

  /**
   * @returns {string} The identifier of a parent element (if provided)
   */
  get parentIdentifier () {
    return this?.dataset?.parentIdentifier
  }

  /**
   * @returns {string} The url, pathname and query parameters
   */
  get sectionUrl () {
    return this.url || `${window.location.pathname}${window.location.search}`
  }

  /**
   * @returns {boolean} Whether or not this section should load when it's needed instead of at pageload
   */
  get isLazy () {
    return this.getAttribute('loading') === 'lazy'
  }

  /**
   * @returns {boolean} Whether or not this section should be cached
   */
  get useCache () {
    return this.hasAttribute('cache-key')
  }

  /**
   * @returns {string} The key to use to cache this section against
   */
  get cacheKey () {
    return this.getAttribute('cache-key')
  }

  /**
   * @returns {number} The length of time this section should be cached for, in hours
   */
  get cacheExpiry () {
    return parseInteger(this.getAttribute('cache-expiry'))
  }

  /**
   * @returns {number} The length of time this section should be cached for, in milliseconds
   */
  get cacheExpiryInMilliseconds () {
    return this.cacheExpiry * 3600000
  }

  /**
   * @returns {string} The cached html for the requested section
   */
  get cachedHtml () {
    const itemString = localStorage.getItem(this.cacheKey)
    if (!itemString) {
      return null
    }
    const item = JSON.parse(itemString)
    const now = new Date()
    if (now.getTime() > item.expiry) {
      localStorage.removeItem(this.cacheKey)
      return null
    }
    return item.value
  }

  /**
   * @returns {boolean} Whether or not IntersectionObserver is supported by the browser
   */
  get isIntersectionObserverSupported () {
    return `IntersectionObserver` in window
  }
}

if (!customElements.get('quick-buy-async')) {
  customElements.define('quick-buy-async', QuickBuyAsync)
}
